// 标签汇总页收益日历的纯函数（AC-110）。每格 = 当日 Σtotal_profit 差，即接口 series 的 daily_pnl。

function pnlOf(point) {
  const value = Number(point?.daily_pnl);
  return Number.isFinite(value) ? value : null;
}

// 某个月的日历格子，只有周一到周五（A 股周末不开盘，用户 2026-10-12 要求去掉周六周日；
// 此前 2026-10-08 曾是日 一 二 三 四 五 六、周日为首）。没有交易点的日子（周末、停牌、区间外）pnl 为 null。
export function monthCells(series, month) {
  const byDate = new Map((series ?? []).map((point) => [point.date, pnlOf(point)]));
  const [year, monthIndex] = month.split("-").map(Number);
  const daysInMonth = new Date(year, monthIndex, 0).getDate();
  const cells = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const weekday = new Date(year, monthIndex - 1, day).getDay();  // 0 = 周日 … 6 = 周六
    if (weekday === 0 || weekday === 6) continue;
    // 当月第一个工作日前面补空格，让它落在周一到周五对应的那一列
    if (!cells.length) cells.push(...Array.from({ length: weekday - 1 }, () => null));
    const date = `${month}-${String(day).padStart(2, "0")}`;
    cells.push({ date, day, pnl: byDate.get(date) ?? null });
  }
  return cells;
}

// 某一年每个月的盈亏合计；没有任何交易点的月份 pnl 为 null。
export function yearCells(series, year) {
  return Array.from({ length: 12 }, (_, index) => {
    const prefix = `${year}-${String(index + 1).padStart(2, "0")}`;
    const values = (series ?? []).filter((p) => p.date.startsWith(prefix)).map(pnlOf).filter((v) => v !== null);
    return { month: prefix, pnl: values.length ? values.reduce((a, b) => a + b, 0) : null };
  });
}

// 金额统一两位小数（日 / 月盈亏只到元会把几十块的盈亏抹成整数）；10 万以上写成「x.xx 万」，日历格子才放得下。
export function formatPnl(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "--";
  const sign = value > 0 ? "+" : "";
  if (Math.abs(value) >= 100000) return `${sign}${(value / 10000).toFixed(2)}万`;
  return `${sign}${value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// 日历格子（窄，1440 屏上一格约 37px，数字 + 汉字合计放得下 7 个半角字符宽，汉字算 2）：
// 随数量级收小数位：亿 2 位、百万以上万 0 位、十万以上万 1 位、万 2 位、千到万只到元、千以下两位小数。
// 不写「+」号（格子本身红涨绿跌已表示正负，负号保留）；完整数字放在格子的悬停提示里（formatPnlFull），不缩写。
export function formatPnlCell(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "--";
  const size = Math.abs(value);
  // 各档的下界取「四舍五入后恰好进位」的临界值，免得 99,999 写成「10.00万」多出一位。
  if (size >= 99995000) return `${(value / 1e8).toFixed(2)}亿`;
  if (size >= 999500) return `${Math.round(value / 1e4)}万`;
  if (size >= 99950) return `${(value / 1e4).toFixed(1)}万`;
  if (size >= 9999.5) return `${(value / 1e4).toFixed(2)}万`;
  if (size >= 999.995) return Math.round(value).toLocaleString("zh-CN");
  return value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatPnlFull(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "--";
  return `${value > 0 ? "+" : ""}${value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// range 是日期前缀："2024-05" 统计这个月，"2024" 统计这一年。
export function calendarStats(series, range) {
  const days = (series ?? [])
    .filter((point) => point.date.startsWith(range))
    .map((point) => ({ date: point.date, pnl: pnlOf(point) }))
    .filter((day) => day.pnl !== null);
  if (!days.length) return { tradingDays: 0, profitDays: 0, lossDays: 0, best: null, worst: null, average: null, total: null };
  const best = days.reduce((a, b) => (b.pnl > a.pnl ? b : a));
  const worst = days.reduce((a, b) => (b.pnl < a.pnl ? b : a));
  return {
    tradingDays: days.length,
    profitDays: days.filter((d) => d.pnl > 0).length,
    lossDays: days.filter((d) => d.pnl < 0).length,
    best,
    worst,
    average: days.reduce((sum, d) => sum + d.pnl, 0) / days.length,
    total: days.reduce((sum, d) => sum + d.pnl, 0),
  };
}

// 年视图下面的统计：按月（有交易点的月份）数盈利 / 亏损月数、最好 / 最差月、月均与全年合计。
export function yearStats(series, year) {
  const months = yearCells(series, year)
    .filter((cell) => cell.pnl !== null)
    .map((cell) => ({ date: cell.month, pnl: cell.pnl }));
  if (!months.length) return { tradingDays: 0, profitDays: 0, lossDays: 0, best: null, worst: null, average: null, total: null };
  const total = months.reduce((sum, m) => sum + m.pnl, 0);
  return {
    tradingDays: months.length,
    profitDays: months.filter((m) => m.pnl > 0).length,
    lossDays: months.filter((m) => m.pnl < 0).length,
    best: months.reduce((a, b) => (b.pnl > a.pnl ? b : a)),
    worst: months.reduce((a, b) => (b.pnl < a.pnl ? b : a)),
    average: total / months.length,
    total,
  };
}
